/**
 * Maps a b2bportal hotel-search query into the commerce-portals stays
 * equivalent. Used by the COMMERCE_PLATFORM_ROLLOUT flag to route Nubank
 * hotel traffic into the commerce-portals stays funnel instead of b2bportal.
 *
 * Two entry points:
 *
 *   - {@link buildCommerceStaysHomeUrl} — entry-time redirect from
 *     `/hotels/search` to commerce-portals' stays search page
 *     (`/stays/search`). Preserves any deeplinked query params so
 *     the search page can pre-fill its form. Use this when the user
 *     lands on the b2bportal hotel-search route and we want the
 *     *entire* funnel to live in commerce-portals (not just
 *     availability+).
 *
 *   - {@link buildCommerceStaysUrl} — submit-time redirect from
 *     b2bportal's submit handler directly to `/stays/availability`,
 *     skipping commerce-portals' search UI entirely. Older path; kept
 *     for backwards compat in case any caller still wires a search
 *     submit through it.
 *
 * The legacy and commerce param shapes don't match 1:1, so we translate
 * via {@link translateLegacyParams}. Anything we don't explicitly
 * recognize is dropped — we never want to pass through an unknown
 * legacy param that the commerce side might interpret unexpectedly.
 */

import { getColorModeCookie } from "./colors";

const EXTERNAL_TRIPS_LIST_QUERY_PARAM = "external_trips_list";

const SIMPLE_PARAM_MAP: Record<string, string> = {
  placeId: "location",
  query: "locationLabel",
  fromDate: "checkInDate",
  untilDate: "checkOutDate",
  adultsCount: "adults",
  roomsCount: "rooms",
};

/**
 * Translates a legacy URLSearchParams into the commerce-portals stays
 * equivalent. Pure function, exported for testing.
 */
export function translateLegacyParams(
  legacyParams: URLSearchParams
): URLSearchParams {
  const commerceParams = new URLSearchParams();

  for (const [legacyKey, commerceKey] of Object.entries(SIMPLE_PARAM_MAP)) {
    const value = legacyParams.get(legacyKey);
    if (value) {
      commerceParams.set(commerceKey, value);
    }
  }

  // childrenAges are repeated params in legacy (e.g. childrenAges=5&childrenAges=8)
  // but comma-separated in commerce (e.g. childrenAges=5,8).
  const childrenAges = legacyParams.getAll("childrenAges");
  if (childrenAges.length > 0) {
    commerceParams.set("childrenAges", childrenAges.join(","));
  }

  commerceParams.set("webview", "true");

  return commerceParams;
}

/**
 * Builds the entry-time redirect target: commerce-portals' stays
 * search page (`/stays/search`). Used when the
 * `COMMERCE_PLATFORM_ROLLOUT` flag fires the moment a Nubank user
 * lands on b2bportal's `/hotels/search`. The search page handles
 * its own search UI; we just hand off any deeplinked query params
 * it can use to pre-fill.
 *
 * Why `/stays/search` and not `/stays/`: the bare `/stays/` index
 * route in commerce-portals requires *both* `stays.enabled` and
 * `home.enabled` in the tenant config, and Nubank disables the
 * portal home (it's a stays-only tenant), so `/stays/` 404s.
 * `/stays/search` only requires `stays.enabled` and is the right
 * landing for stays-only tenants. Reference:
 * commerce-portals `apps/portal/src/routes/stays.ts`.
 *
 * Origin handling:
 * - In production / staging deploys, `VITE_COMMERCE_PORTAL_BASE_URL`
 *   is explicitly empty (`.env.staging` / `.env.production`) and the
 *   redirect stays relative — istio gateway forwards `/stays/...`
 *   on the b2bportal host to commerce-portals' service.
 * - In local dev, no such gateway exists, so a relative redirect
 *   would land on `localhost:3003/stays/search` (which b2bportal
 *   doesn't serve, and its router would bounce to home). Set
 *   `VITE_COMMERCE_PORTAL_BASE_URL` (e.g.
 *   `https://nubank.commerce.staging.hopper.com` or
 *   `http://localhost:5173`) to make the redirect absolute and
 *   target a real commerce-portals deploy from local. The
 *   dedicated yarn scripts (`start:nubank:*:commerce-{local,staging}`)
 *   already set this for you.
 * - Allowlist (SEC-002): only `*.hopper.com` and `localhost:<port>`
 *   are honored. Any other value falls back to the relative URL
 *   with a console.error so misconfiguration is loud.
 *
 * @example
 *   // No deeplink, no override:
 *   buildCommerceStaysHomeUrl(new URLSearchParams())
 *   // → "/stays/search"
 *
 *   // Deeplink from a Nubank push notification:
 *   buildCommerceStaysHomeUrl(
 *     new URLSearchParams("placeId=ChIJ...&query=Sao+Paulo&fromDate=2026-05-10&untilDate=2026-05-12&adultsCount=2")
 *   )
 *   // → "/stays/search?location=ChIJ...&locationLabel=Sao+Paulo&checkInDate=2026-05-10&checkOutDate=2026-05-12&adults=2"
 *
 *   // With VITE_COMMERCE_PORTAL_BASE_URL set (local dev):
 *   // → "https://nubank.commerce.staging.hopper.com/stays/search?…"
 */
/**
 * Allowlist of permitted origins for the
 * `VITE_COMMERCE_PORTAL_BASE_URL` override. Defends against an
 * open-redirect vulnerability (SEC-002): a misconfigured or
 * supply-chain-compromised build could otherwise concatenate any
 * arbitrary host into our `window.location.replace` target,
 * silently shipping a redirect-to-attacker bundle.
 *
 * Only `*.hopper.com` (real deployments) and `localhost:<port>`
 * (local dev) are accepted. Any other value falls back to the
 * relative URL (production-equivalent behavior).
 */
// `[a-z0-9-]+` rather than `[\w-]+` on purpose: `\w` includes
// underscore and (depending on flags) unicode word characters,
// neither of which appear in legitimate Hopper subdomains.
// Restricting to ASCII alphanumeric + hyphen rules out a class
// of homograph / underscore-trick lookalikes.
//
// SEC-006 mitigation: `*.hopper.com` origins must be HTTPS;
// `http://` is only allowed for `localhost` (local dev). This
// closes the plaintext-redirect door for production hostnames
// without breaking local dev workflows.
const ALLOWED_BASE_URL_PATTERN =
  /^(https:\/\/[a-z0-9-]+(\.[a-z0-9-]+)*\.hopper\.com|https?:\/\/localhost(:\d+)?)$/;

function resolveBaseUrl(): string {
  const raw = import.meta.env.VITE_COMMERCE_PORTAL_BASE_URL ?? "";
  if (raw === "") return "";
  if (ALLOWED_BASE_URL_PATTERN.test(raw)) return raw;
  // SEC-002: never silently honor an off-allowlist origin. Falling
  // back to the relative URL is the safe production-equivalent
  // behavior, and the console.error makes the misconfiguration
  // loud during dev / build review.

  console.error(
    `[commercePlatformRedirect] VITE_COMMERCE_PORTAL_BASE_URL "${raw}" is not in the allowlist; falling back to relative URL.`
  );
  return "";
}

/**
 * Reads the b2bportal color-mode cookie and, if valid, appends it as a
 * `color_mode` query param so the commerce portal can initialize its
 * theme to match. Without this, dark mode is lost during the redirect
 * because the two apps use different storage mechanisms (cookie vs.
 * query-param → localStorage).
 */
function appendColorModeIfPresent(params: URLSearchParams): void {
  const colorMode = getColorModeCookie()?.toLowerCase();
  if (colorMode === "dark" || colorMode === "light") {
    params.set("color_mode", colorMode);
  }
}

// Nubank is a pt-BR-only tenant (see branding.ts `locale: "pt-BR"` and
// App.tsx's `DEFAULT_LOCALE`). commerce-portals routes accept an optional
// `/xx-XX` locale prefix (see nupayCommerceCheckoutPath.ts); without it,
// commerce-portals falls back to its own default locale detection instead
// of respecting Nubank's fixed pt-BR tenant locale.
const COMMERCE_LOCALE_PATH_PREFIX = "/pt-BR";

export function buildCommerceStaysHomeUrl(
  legacySearchParams: URLSearchParams,
  externalTripsListEnabled: boolean = false
): string {
  const commerceParams = translateLegacyParams(legacySearchParams);
  commerceParams.set(
    EXTERNAL_TRIPS_LIST_QUERY_PARAM,
    String(externalTripsListEnabled)
  );
  appendColorModeIfPresent(commerceParams);

  const qs = commerceParams.toString();
  const basePath = `${COMMERCE_LOCALE_PATH_PREFIX}/stays/search`;
  const path = qs ? `${basePath}?${qs}` : basePath;
  return `${resolveBaseUrl()}${path}`;
}

const FLIGHT_SIMPLE_PARAM_MAP: Record<string, string> = {
  origin: "origin",
  destination: "destination",
  departureDate: "departureDate",
  returnDate: "returnDate",
  adultsCount: "adults",
  childrenCount: "children",
  infantsInSeatCount: "seatedInfants",
  infantsOnLapCount: "onLapInfants",
  originLabel: "originLabel",
  destinationLabel: "destinationLabel",
};

/**
 * Translates legacy b2bportal flight-search query params into the
 * commerce-portals air search equivalent. Pure function, exported for testing.
 */
export function translateLegacyFlightParams(
  legacyParams: URLSearchParams
): URLSearchParams {
  const commerceParams = new URLSearchParams();

  for (const [legacyKey, commerceKey] of Object.entries(
    FLIGHT_SIMPLE_PARAM_MAP
  )) {
    const value = legacyParams.get(legacyKey);
    if (value) {
      commerceParams.set(commerceKey, value);
    }
  }

  const tripCategory = legacyParams.get("tripCategory")?.toLowerCase();
  if (tripCategory === "one_way" || tripCategory === "one-way") {
    commerceParams.set("tripType", "one-way");
    commerceParams.delete("returnDate");
  } else if (
    tripCategory === "round_trip" ||
    tripCategory === "return_trip" ||
    tripCategory === "round-trip"
  ) {
    commerceParams.set("tripType", "round-trip");
  } else if (commerceParams.has("returnDate")) {
    commerceParams.set("tripType", "round-trip");
  } else {
    commerceParams.set("tripType", "one-way");
  }

  if (commerceParams.has("origin")) {
    commerceParams.set(
      "originType",
      legacyParams.get("originType")?.toLowerCase() === "city"
        ? "City"
        : "Airport"
    );
  }
  if (commerceParams.has("destination")) {
    commerceParams.set(
      "destinationType",
      legacyParams.get("destinationType")?.toLowerCase() === "city"
        ? "City"
        : "Airport"
    );
  }

  commerceParams.set("webview", "true");

  return commerceParams;
}

/**
 * Builds the entry-time redirect target: commerce-portals' air search page
 * (`/air/search`). Mirrors {@link buildCommerceStaysHomeUrl} for the flights
 * funnel. `/air/search` only requires `air.enabled` in tenant config (not
 * `home.enabled`), same pattern as stays-only tenants using `/stays/search`.
 */
export function buildCommerceAirHomeUrl(
  legacySearchParams: URLSearchParams,
  externalTripsListEnabled: boolean = false
): string {
  const commerceParams = translateLegacyFlightParams(legacySearchParams);
  commerceParams.set(
    EXTERNAL_TRIPS_LIST_QUERY_PARAM,
    String(externalTripsListEnabled)
  );
  appendColorModeIfPresent(commerceParams);

  const qs = commerceParams.toString();
  const basePath = `${COMMERCE_LOCALE_PATH_PREFIX}/air/search`;
  const path = qs ? `${basePath}?${qs}` : basePath;
  return `${resolveBaseUrl()}${path}`;
}

function isSupportedLegacyLocationType(value: string | null): boolean {
  const normalizedValue = value?.toLowerCase();
  return normalizedValue === "city" || normalizedValue === "airport";
}

function isNonnegativeInteger(value: string): boolean {
  return /^(0|[1-9]\d*)$/.test(value) && Number.isSafeInteger(Number(value));
}

function isPositiveInteger(value: string | null): boolean {
  return (
    value !== null &&
    /^[1-9]\d*$/.test(value) &&
    Number.isSafeInteger(Number(value))
  );
}

function hasSupportedLegacyTripCategory(params: URLSearchParams): boolean {
  if (!params.has("tripCategory")) {
    return true;
  }

  const tripCategory = params.get("tripCategory")?.toLowerCase();
  return [
    "one_way",
    "one-way",
    "round_trip",
    "round-trip",
    "return_trip",
  ].includes(tripCategory ?? "");
}

function isCanonicalDate(value: string | null): value is string {
  if (value === null || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return false;
  }

  const date = new Date(`${value}T00:00:00Z`);
  return (
    !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value
  );
}

function hasValidLegacyDates(
  params: URLSearchParams,
  tripType: string | null
): boolean {
  const departureDate = params.get("departureDate");
  const returnDate = params.get("returnDate");
  if (!isCanonicalDate(departureDate)) {
    return false;
  }

  return (
    tripType !== "round-trip" ||
    (isCanonicalDate(returnDate) && returnDate >= departureDate)
  );
}

function hasValidLegacyPassengerCounts(params: URLSearchParams): boolean {
  if (!isPositiveInteger(params.get("adultsCount"))) {
    return false;
  }

  return ["childrenCount", "infantsInSeatCount", "infantsOnLapCount"].every(
    (param) => {
      const value = params.get(param);
      return !value || isNonnegativeInteger(value);
    }
  );
}

/**
 * Builds the shop-entry redirect target for the commerce-portals air funnel.
 * Mirrors {@link buildCommerceAirHomeUrl}, but routes to `/air/shop` when the
 * legacy search has complete, valid criteria and falls back to `/air/search`
 * otherwise. Complete criteria include origin and destination with supported
 * location types, a canonical departure date, a positive adult count, and a
 * supported trip type. Round trips also require a return date on or after the
 * departure date.
 *
 * @param legacySearchParams Legacy b2bportal flight-search query parameters.
 * @param externalTripsListEnabled Whether Commerce should enable external trips.
 */
export function buildCommerceAirShopEntryUrl(
  legacySearchParams: URLSearchParams,
  externalTripsListEnabled: boolean = false
): string {
  const commerceParams = translateLegacyFlightParams(legacySearchParams);
  commerceParams.set(
    EXTERNAL_TRIPS_LIST_QUERY_PARAM,
    String(externalTripsListEnabled)
  );
  appendColorModeIfPresent(commerceParams);

  const requiredParams = [
    "origin",
    "originType",
    "destination",
    "destinationType",
    "departureDate",
    "adults",
    "tripType",
  ];
  const hasCompleteCriteria =
    isSupportedLegacyLocationType(legacySearchParams.get("originType")) &&
    isSupportedLegacyLocationType(legacySearchParams.get("destinationType")) &&
    hasSupportedLegacyTripCategory(legacySearchParams) &&
    hasValidLegacyDates(legacySearchParams, commerceParams.get("tripType")) &&
    hasValidLegacyPassengerCounts(legacySearchParams) &&
    requiredParams.every((param) => Boolean(commerceParams.get(param))) &&
    (commerceParams.get("tripType") !== "round-trip" ||
      Boolean(commerceParams.get("returnDate")));
  const entryPath = hasCompleteCriteria ? "shop" : "search";

  return `${resolveBaseUrl()}${COMMERCE_LOCALE_PATH_PREFIX}/air/${entryPath}?${commerceParams.toString()}`;
}

/**
 * Builds the redirect target for a single flight's trip details:
 * commerce-portals' air trip-details page (`/air/trips/{orderId}`).
 * Used when the `COMMERCE_PLATFORM_FLIGHTS_MY_TRIPS_DETAILS` flag fires as a
 * Nubank user opens flight trip details in the legacy My Trips
 * (card tap or `?tripId=` deeplink), so post-booking servicing —
 * including CFAR HTS, which only exists on the commerce platform —
 * is served by commerce-portals. [NU-403]
 *
 * The identifier is the legacy `bookedItinerary.id`, which equals the
 * commerce `orderId`/`tripId` (commerce-portals itself uses the same
 * id in the reverse redirect: `/trips?tripId={itineraryId}`). The
 * locale path segment is optional in commerce-portals routing, so it
 * is omitted here — the portal resolves the session locale itself.
 *
 * Origin handling and the `webview` / `color_mode` params follow
 * {@link buildCommerceAirHomeUrl}.
 */
export function buildCommerceAirTripDetailsUrl(
  itineraryId: string,
  externalTripsListEnabled: boolean = false
): string {
  const commerceParams = new URLSearchParams();
  commerceParams.set("webview", "true");
  commerceParams.set(
    EXTERNAL_TRIPS_LIST_QUERY_PARAM,
    String(externalTripsListEnabled)
  );
  appendColorModeIfPresent(commerceParams);

  const path = `/air/trips/${encodeURIComponent(itineraryId)}`;
  return `${resolveBaseUrl()}${path}?${commerceParams.toString()}`;
}

/**
 * Builds the redirect target for a flight post-booking action:
 * commerce-portals' air action route
 * (`/air/trips/{orderId}/actions/{action}`). Used by the native
 * WebView entry (`/auth/startsession/` + action-shaped
 * `redirect_uri`) when `COMMERCE_PLATFORM_FLIGHTS_MY_TRIPS_DETAILS` is on for
 * the user, so CFAR / DAFAR / cancel are served by the commerce
 * platform. [NU-470]
 *
 * Identifier semantics, locale omission (the portal resolves the
 * session locale; the segment is optional in commerce routing), and
 * the origin / `webview` / `color_mode` handling all follow
 * {@link buildCommerceAirTripDetailsUrl}.
 */
export function buildCommerceFlightActionUrl(
  orderId: string,
  action: string,
  externalTripsListEnabled: boolean = false
): string {
  const commerceParams = new URLSearchParams();
  commerceParams.set("webview", "true");
  commerceParams.set(
    EXTERNAL_TRIPS_LIST_QUERY_PARAM,
    String(externalTripsListEnabled)
  );
  appendColorModeIfPresent(commerceParams);

  const path = `/air/trips/${encodeURIComponent(
    orderId
  )}/actions/${encodeURIComponent(action)}`;
  return `${resolveBaseUrl()}${path}?${commerceParams.toString()}`;
}

/**
 * Transforms a legacy b2bportal hotel availability URL into a
 * commerce-portals stays availability URL.
 *
 * Legacy format: hotels/availability/sao-paulo?fromDate=2026-04-10&untilDate=2026-04-12&adultsCount=2&childrenCount=2&childrenAges=5&childrenAges=8&roomsCount=1&placeId=ChIJ...&query=S%C3%A3o+Paulo&view=list
 * Commerce format: /stays/availability?checkInDate=2026-04-10&checkOutDate=2026-04-12&adults=2&childrenAges=5,8&rooms=1&location=ChIJ...&locationLabel=S%C3%A3o+Paulo
 *
 * @deprecated Prefer {@link buildCommerceStaysHomeUrl} for the
 * entry-time redirect (the whole funnel lives in commerce-portals).
 * This function is retained for any caller that still wires a
 * submit-time redirect from b2bportal directly into stays availability.
 */
export function buildCommerceStaysUrl(legacyPath: string): string | null {
  let params: URLSearchParams;
  try {
    const legacyUrl = new URL(legacyPath, "http://localhost");
    params = legacyUrl.searchParams;
  } catch {
    return null;
  }
  const commerceParams = translateLegacyParams(params);
  appendColorModeIfPresent(commerceParams);

  return `/stays/availability?${commerceParams.toString()}`;
}
